import { EventInput, ShiftInput } from '@amzn/red-velvet-api';
import { useUpdateEventDraft } from './draftEvent';
import { useCreateEvent } from './createEvent';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as sanitizer from '@amzn/aws-console-html-sanitizer';
import {
  SaveStatus,
  SAVE_STATUS_IDLE,
  SAVE_STATUS_SAVING,
  SAVE_STATUS_SAVED,
  SAVE_STATUS_ERROR,
} from '../../utils/constants';
import { publishKatalMetric } from '../../components/katalAnalytics';
import { MAX_COORDINATORS } from '../../components/events/createEvent/constants';

// Smithy @length limits for EventInput and ShiftInput fields
const MAX_TITLE = 255;
const MAX_SHORT_DESCRIPTION = 100;
const MAX_SHIFT_NAME = 128;

interface QueuedSave {
  event?: EventInput;
  shifts?: ShiftInput[];
  onSuccess?: () => void;
}

interface UseAutoSaveEventDraftProps {
  eventId: React.MutableRefObject<string>;
  metricPrefix: string;
  setHasUnsavedChanges: (value: boolean) => void;
  onShiftIdsSynced?: (shiftIds: string[]) => void;
  onSaveError?: (error: Error) => void;
  onEventCreated?: () => void;
}

export function useAutoSaveEventDraft({
  eventId,
  metricPrefix,
  setHasUnsavedChanges,
  onShiftIdsSynced,
  onSaveError,
  onEventCreated,
}: UseAutoSaveEventDraftProps) {
  const { mutate: updateEventDraft } = useUpdateEventDraft();
  const { mutate: createEvent } = useCreateEvent();
  const isCreating = useRef<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(SAVE_STATUS_IDLE);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);

  // Pending saves buffered while the draft is being created
  const pendingEventSave = useRef<EventInput | null>(null);
  const pendingShiftSave = useRef<ShiftInput[] | null>(null);

  // Prevents the save indicator from flickering between rapid edits.
  const inFlight = useRef(false);
  const queued = useRef<QueuedSave | null>(null);

  const markSaved = () => {
    setSaveStatus(SAVE_STATUS_SAVED);
    setLastSavedTime(new Date());
  };

  const truncateEvent = (event: EventInput): EventInput => {
    const next = { ...event };
    if (next.title === '') next.title = undefined;
    if (next.title) next.title = next.title.slice(0, MAX_TITLE);
    if (next.description)
      next.description = sanitizer.sanitizeHtmlString(next.description, {
        keepFormattingTags: true,
      });
    if (next.shortDescription)
      next.shortDescription = next.shortDescription.slice(0, MAX_SHORT_DESCRIPTION);
    if (next.timeZone === '') next.timeZone = undefined;
    if (next.additionalCoordinators)
      next.additionalCoordinators = next.additionalCoordinators.slice(0, MAX_COORDINATORS);
    return next;
  };

  const truncateShifts = (shifts: ShiftInput[]): ShiftInput[] =>
    shifts.map((s) =>
      s.name && s.name.length > MAX_SHIFT_NAME ? { ...s, name: s.name.slice(0, MAX_SHIFT_NAME) } : s
    );

  const saveDraft = (event?: EventInput, shifts?: ShiftInput[], onSuccessCallback?: () => void) => {
    if (eventId.current === '') return;
    setHasUnsavedChanges(true);

    const payload: QueuedSave = {
      event: event ? truncateEvent(event) : undefined,
      shifts: shifts ? truncateShifts(shifts) : undefined,
      onSuccess: onSuccessCallback,
    };

    // Already saving; queue the latest state so the user's edits aren't lost.
    if (inFlight.current) {
      queued.current = {
        event: payload.event ?? queued.current?.event,
        shifts: payload.shifts ?? queued.current?.shifts,
        onSuccess: payload.onSuccess ?? queued.current?.onSuccess,
      };
      return;
    }

    executeSave(payload);
  };

  const executeSave = (payload: QueuedSave) => {
    inFlight.current = true;
    setSaveStatus(SAVE_STATUS_SAVING);

    updateEventDraft(
      {
        eventId: eventId.current,
        ...(payload.event && { event: payload.event }),
        ...(payload.shifts && { shifts: payload.shifts }),
      },
      {
        onSuccess: (response) => {
          // Only show "saved" when the queue is empty so the UI doesn't claim a stale state.
          if (!queued.current) {
            setHasUnsavedChanges(false);
            markSaved();
          }
          if (response?.shiftIds?.length && onShiftIdsSynced) {
            onShiftIdsSynced(response.shiftIds);
          }
          payload.onSuccess?.();
        },
        onError: (error) => {
          // Don't flash an error if another save is about to fire.
          if (!queued.current) {
            publishKatalMetric(`${metricPrefix}_autoSaveError`, error.message || 'unknown');
            onSaveError?.(error);
            setSaveStatus(SAVE_STATUS_ERROR);
          }
        },
        onSettled: () => {
          inFlight.current = false;
          const next = queued.current;
          queued.current = null;
          if (next) executeSave(next);
        },
      }
    );
  };

  // Save any changes that came in while the draft was being created
  const flushPendingSaves = () => {
    const pendingEvent = pendingEventSave.current;
    const pendingShifts = pendingShiftSave.current;
    pendingEventSave.current = null;
    pendingShiftSave.current = null;
    if (pendingEvent || pendingShifts) {
      saveDraft(pendingEvent ?? undefined, pendingShifts ?? undefined);
    }
  };

  const autoSaveHandler = (updatedEventDetails: EventInput) => {
    setHasUnsavedChanges(true);

    // An empty eventId means that this is the first time the event is being saved
    if (updatedEventDetails.title !== '' && eventId.current === '' && !isCreating.current) {
      isCreating.current = true;
      setSaveStatus(SAVE_STATUS_SAVING);
      createEvent(
        { title: updatedEventDetails.title as string },
        {
          onSuccess: (response) => {
            eventId.current = response.eventId;
            setHasUnsavedChanges(false);
            isCreating.current = false;
            onEventCreated?.();
            markSaved();

            // Update the url params to include the generated draft id
            const newParams = new URLSearchParams(searchParams);
            newParams.set('draftId', response.eventId);
            setSearchParams(newParams);

            flushPendingSaves();
          },
          onError: (error) => {
            isCreating.current = false;
            pendingEventSave.current = null;
            pendingShiftSave.current = null;
            publishKatalMetric(`${metricPrefix}_autoSaveError`, error.message || 'unknown');
            onSaveError?.(error);
            setSaveStatus(SAVE_STATUS_ERROR);
          },
        }
      );
    } else if (isCreating.current || eventId.current === '') {
      // Draft creation in progress or no title yet, then stash the latest fields
      pendingEventSave.current = updatedEventDetails;
    } else {
      saveDraft(updatedEventDetails);
    }
  };

  const autoSaveShiftsHandler = (shifts: ShiftInput[]) => {
    if (isCreating.current || eventId.current === '') {
      pendingShiftSave.current = shifts;
      return;
    }
    saveDraft(undefined, shifts);
  };

  return { autoSaveHandler, autoSaveShiftsHandler, saveDraft, saveStatus, lastSavedTime };
}
